import { ScrollArea } from "@/components/ui/scroll-area";

const PrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <ScrollArea className="h-[calc(100vh-8rem)] rounded-md border p-8">
        <div className="max-w-3xl mx-auto prose prose-slate dark:prose-invert">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Effective Date: February 2, 2025<br />
            Last Updated: February 2, 2025
          </p>

          <h2 className="text-2xl font-semibold mt-8">1. Introduction</h2>
          <p>
            Welcome to <strong>www.tryposit.com</strong> ("Posit," "we," "our," or "us"), operated by <strong>One Interview Inc.</strong> This Privacy Policy describes how we collect, use, and share information when you use our website and services.
          </p>

          <h2 className="text-2xl font-semibold mt-8">2. Information We Collect</h2>
          <p>We collect the following types of information:</p>
          <ul>
            <li><strong>Personal Information</strong>: When you sign up, we may collect your name and email address. Payments are handled by <strong>Stripe</strong>; we do not store full card details.</li>
            <li><strong>User Content</strong>: Data you provide when using Posit (e.g., prompts, files, messages).</li>
            <li><strong>Usage & Device Information</strong>: Basic technical data such as browser type, approximate location (derived from IP), and pages viewed. Analytics cookies are <strong>optional</strong> and only enabled if you consent. Before a choice is made, we may collect <em>cookieless</em> page view counts (no identifiers, memory-only).</li>
            <li><strong>Affiliate & Referral Data</strong>: If you arrive via a referral link (e.g., <code>?ref=CODE</code>) we may store the code locally for up to 30 days to attribute signups and purchases. We may log click metadata (IP address, user agent, referrer, landing page, session ID) to prevent fraud and calculate commissions.</li>
            <li><strong>AI Processing Data</strong>: We send relevant user content to <strong>OpenAI's API</strong> to generate responses. Do not upload confidential information.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8">3. How We Use Your Information</h2>
          <ul>
            <li>Provide, operate, and improve Posit’s AI features.</li>
            <li>Process payments, subscriptions, and creator payouts (Stripe and Stripe Connect).</li>
            <li>Measure product performance and fix issues (analytics only with consent).</li>
            <li>Attribute affiliate referrals and calculate commissions.</li>
            <li>Comply with legal obligations and prevent fraud/abuse.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8">4. Cookies, Analytics, and Consent</h2>
          <p>
            We use a consent banner to control analytics cookies. If you select “Yes,” analytics cookies may be set; if “No,” only essential cookies are used and we operate in a cookieless, minimal mode (no identifiers, no session replay). You can change your choice at any time in your browser (clear site data) or via a future “Manage cookies” link.
          </p>
          <ul>
            <li><strong>Tag Management</strong>: We use <strong>Google Tag Manager</strong> with Consent Mode to respect your choices.</li>
            <li><strong>Product Analytics</strong>: We use <strong>PostHog</strong> to understand feature usage and improve the product. Session replay is disabled.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8">5. Data Storage & Security</h2>
          <ul>
            <li>User data is stored securely on <strong>Supabase</strong>.</li>
            <li>We implement industry-standard security measures but cannot guarantee absolute security.</li>
            <li>Users are encouraged to use strong, unique passwords and enable two-factor authentication where possible.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8">6. Data Retention & Deletion</h2>
          <ul>
            <li>We retain user data as long as necessary for service functionality.</li>
            <li>Affiliate click logs and fraud-prevention metadata may be retained up to <strong>90 days</strong>. Transaction and payout records may be retained per tax and accounting requirements.</li>
            <li>After account deletion, we retain certain data for compliance and security purposes, typically up to <strong>12 months</strong>, unless law requires longer.</li>
            <li>You may request data deletion by contacting <strong>posit-support@tryposit.com</strong>.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8">7. Third-Party Services</h2>
          <p>
            We use third-party providers such as <strong>OpenAI</strong> (AI processing), <strong>Supabase</strong> (hosting), <strong>Stripe / Stripe Connect</strong> (payments and payouts), <strong>Google Tag Manager</strong> (tag management), and <strong>PostHog</strong> (product analytics). Refer to their privacy policies for details.
          </p>

          <h2 className="text-2xl font-semibold mt-8">8. Global Operations & Compliance</h2>
          <ul>
            <li>Posit operates <strong>globally</strong> and processes user data in different regions.</li>
            <li>Users from the <strong>EU/EEA</strong> have rights under <strong>GDPR</strong>, including the right to access, correct, or delete their data.</li>
            <li><strong>CCPA Compliance</strong>: California residents have rights regarding personal data under the California Consumer Privacy Act.</li>
            <li><strong>Consent Withdrawal</strong>: Where processing relies on consent (e.g., analytics cookies), you can withdraw at any time.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8">9. Children’s Privacy</h2>
          <p>
            Our services are not directed to children under 13. If you believe a child has provided personal data, contact us to request deletion.
          </p>

          <h2 className="text-2xl font-semibold mt-8">10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy. Users will be notified of significant changes via email or a website notification. Continued use of Posit constitutes acceptance of any changes.
          </p>
        </div>
      </ScrollArea>
    </div>
  );
};

export default PrivacyPolicy;